/**
 * Type guard for GeoJSON Position pairs: `[longitude, latitude]`.
 */
export const isValidCoordinatePair = (
	coordinates: [number, number] | number[] | undefined,
): coordinates is [number, number] => {
	// GeoJSON Position is `[lng, lat]` or `[lng, lat, elevation]`.
	if (!coordinates || coordinates.length < 2) return false;

	const lng = coordinates[0];
	const lat = coordinates[1];
	return (
		typeof lng === "number" &&
		typeof lat === "number" &&
		!Number.isNaN(lng) &&
		!Number.isNaN(lat) &&
		Number.isFinite(lng) &&
		Number.isFinite(lat) &&
		lat >= -90 &&
		lat <= 90 &&
		lng >= -180 &&
		lng <= 180
	);
};

/** Validation message for a latitude input string, or undefined if empty/valid. */
export const getLatitudeError = (latitude: string): string | undefined => {
	if (!latitude.trim()) return undefined;
	const value = parseFloat(latitude);
	if (Number.isNaN(value)) return "Must be a valid number";
	if (value < -90 || value > 90) return "Latitude must be between -90 and 90";
	return undefined;
};

/** Validation message for a longitude input string, or undefined if empty/valid. */
export const getLongitudeError = (longitude: string): string | undefined => {
	if (!longitude.trim()) return undefined;
	const value = parseFloat(longitude);
	if (Number.isNaN(value)) return "Must be a valid number";
	if (value < -180 || value > 180)
		return "Longitude must be between -180 and 180";
	return undefined;
};

/**
 * Collect lng/lat vertices used to frame the camera.
 * Points, zone fill polygons, and line-boundary overlays all participate.
 */
/** Normalize a GeoJSON Position to a plain `[lng, lat]` pair for camera math. */
const toFitCoordinate = (
	coordinates: number[] | undefined,
): [number, number] | undefined => {
	if (!isValidCoordinatePair(coordinates)) return undefined;
	return [coordinates[0], coordinates[1]];
};

export const coordinatesFromMapFitFeature = (
	feature: GeoJSON.Feature,
): [number, number][] => {
	const { geometry } = feature;
	if (!geometry) return [];

	const collect = (positions: number[][]): [number, number][] =>
		positions
			.map((position) => toFitCoordinate(position))
			.filter((position): position is [number, number] => position != null);

	switch (geometry.type) {
		case "LineString":
		case "MultiPoint":
			return collect(geometry.coordinates);
		case "MultiLineString":
			return collect(geometry.coordinates.flat());
		case "MultiPolygon":
			return collect(
				geometry.coordinates.flatMap((polygon) => polygon[0] ?? []),
			);
		case "Point": {
			const pair = toFitCoordinate(geometry.coordinates);
			return pair ? [pair] : [];
		}
		case "Polygon":
			return collect(geometry.coordinates[0] ?? []);
		default:
			return [];
	}
};
